import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { FileCheck2, Repeat, Handshake, Wrench, ExternalLink } from 'lucide-react';

const WORKFLOWS = [
  {
    icon: FileCheck2,
    label: 'Agent-issued purchase orders',
    body: 'Every agent-generated PO carries a signed KYPA credential scoped by amount, category, and supplier allow-list. Suppliers verify the PO cryptographically before invoicing.',
  },
  {
    icon: Repeat,
    label: 'AP / AR reconciliation',
    body: 'Every settled transaction produces an on-chain audit record: signed mandate, amount, category, supplier, timestamp. Reconciliation becomes a BaseScan query, not a spreadsheet reconciliation.',
  },
  {
    icon: Handshake,
    label: 'Supplier onboarding',
    body: 'Suppliers post a public USDC pledge before they can transact. Non-custodial. Public. Faster than a commercial credit lookup, and every SLA breach is recorded next to it.',
  },
  {
    icon: Wrench,
    label: 'Payment execution & SLA breach',
    body: 'When a supplier misses an SLA on a mandate-authorized PO, the breach is recorded publicly next to the supplier pledge, a public commitment signal. Remedies stay with your contract.',
  },
];

const WORKED_EXAMPLE = [
  { label: 'Buyer', body: 'Enterprise procurement agent · signed mandate: max $12,000, category "market data", supplier allow-list of 4 vendors, expiry Q4-2026' },
  { label: 'Seller', body: 'Data-vendor agent · Silver KYPA pledge of $25,000 USDC posted on Base mainnet · SLA: 99.9% uptime, sub-500ms latency' },
  { label: 'Trigger', body: 'Vendor feed lags 4–6s for 72 hours mid-quarter · verifiable SLA breach recorded by third-party monitor' },
  { label: 'Resolution', body: 'Buyer files on-chain dispute with Merkle proof · oracle verifies · breach recorded next to vendor pledge · evidence available for your contract remedy · vendor pledge tier decays automatically' },
];

export default function ForB2BProcurement() {
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="for-b2b-procurement-page">
      <Helmet>
        <title>For B2B Procurement: KYPA Credentials for Agent-Issued POs, Invoicing, and Reconciliation</title>
        <meta name="description" content="B2B is the first proving ground for agentic commerce. Procurement, invoicing, reconciliation, supplier onboarding, and payment execution are already rules-based, permissioned, and data-rich. Kojiru's KYPA credentials drop into every one of those workflows as a cryptographic mandate signature plus an on-chain slashable supplier bond." />
        <link rel="alternate" type="application/ld+json" href="/kojiru-schema.jsonld" />
        <link rel="alternate" type="application/json" href="/.well-known/kypa" title="KYPA Protocol Manifest" />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <section className="pt-24 pb-10 px-6 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-4" data-testid="hero-eyebrow">
              FOR B2B PROCUREMENT &middot; THE FIRST PROVING GROUND
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-6 tracking-tight" data-testid="hero-headline">
              B2B is <span className="text-amber-400">where agentic commerce ships first.</span><br />
              KYPA is the credential every PO already needed.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl" data-testid="hero-body">
              The world&rsquo;s largest payment network publicly identified B2B workflows: <em>procurement, invoicing, reconciliation, supplier onboarding, and payment execution</em> as the natural first proving ground for agentic commerce, because they are already rules-based, permissioned, and data-rich (September 2026). a venture firm&rsquo;s September 2026 market research reached the same conclusion. Kojiru drops a signed KYPA credential onto every agent-issued purchase order, converts the supplier-side operational bond into a public on-chain pledge, and records every SLA breach next to that pledge. <strong className="text-white">Live for today. Ready for the agentic wave when it arrives.</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* B2B WORKFLOWS */}
      <section className="py-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">FOUR B2B WORKFLOWS · ONE PRIMITIVE</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-8 leading-tight">Every rules-based B2B workflow the industry named. One KYPA credential.</h2>
          <div className="grid md:grid-cols-2 gap-5" data-testid="workflows-grid">
            {WORKFLOWS.map((r) => {
              const Icon = r.icon;
              return (
                <div key={r.label} className="border border-white/10 rounded-2xl p-6 bg-slate-900/40" data-testid={`workflow-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Icon className="w-5 h-5 text-amber-400" />
                    <div className="text-white font-bold">{r.label}</div>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{r.body}</p>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-6 italic">
            Framing drawn from public statements by the world&rsquo;s largest payment network (September 2026) and a venture firm&rsquo;s September 2026 market research report on agentic commerce and agentic fintech.
          </p>
        </div>
      </section>

      {/* WORKED EXAMPLE */}
      <section className="py-14 px-6 bg-slate-900/50 border-y border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-amber-400 mb-3">THE WORKED EXAMPLE</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">
            A $12,000 quarterly market-data PO.<br />
            The supplier breaches SLA. The evidence is on record.
          </h2>
          <div className="border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-white/5">
                {WORKED_EXAMPLE.map((row) => (
                  <tr key={row.label}>
                    <td className="p-4 text-slate-400 w-1/4 align-top font-bold uppercase tracking-wider text-xs">{row.label}</td>
                    <td className="p-4 text-slate-200 leading-relaxed">{row.body}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-slate-300 leading-relaxed mt-6">
            No MSA renegotiation. No vendor-management escalation. No 47-day arbitration cycle. The mandate the buyer signed at PO time carried the SLA scope; the pledge the seller posted at onboarding carried the operational bond; the on-chain oracle recorded the breach. <strong className="text-white">Evidence on record. Remedies stay with your contract.</strong>
          </p>
        </div>
      </section>

      {/* MID-TASK FUNDING: Wadinski framing (institutional attribution) */}
      <section className="py-14 px-6" data-testid="mid-task-funding-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-amber-400 mb-3">THE MID-TASK FUNDING PROBLEM</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">
            Card delegation sizes to your worst week.<br />
            <span className="text-amber-400">KYPA sizes to the mandate.</span>
          </h2>
          <p className="text-slate-300 leading-relaxed mb-5 max-w-3xl">
            An agentic-commerce industry voice publicly named the underlying trade-off in September 2026, in the context of a KYB identity API launching agent-initiated card-delegation payments. Three constraints make fixed-block pre-buying a structural guess:
          </p>
          <ul className="text-slate-300 leading-relaxed mb-5 max-w-3xl space-y-2 list-disc pl-6">
            <li><strong className="text-white">Unpredictable volume.</strong> Agent-initiated workloads scale externally: five items on Monday, five hundred on Tuesday when a marketing campaign, seasonal rush, or fraud ring hits an inbound queue. The agent has to check every one.</li>
            <li><strong className="text-white">Unpredictable depth.</strong> A clean item costs one API call; a risky one triggers a chain of downstream calls the agent could not have priced in advance.</li>
            <li><strong className="text-white">Pre-buying is a guess.</strong> Under-buy and the queue stalls until a human tops up. Over-buy and capital sits idle. The recommended card-delegation mitigation is <em>"size against your worst onboarding week, not your average one"</em> an operational concession that delegation over-provisions capital by design.</li>
          </ul>
          <div className="border border-amber-500/25 rounded-2xl p-6 bg-amber-500/[0.04] max-w-3xl" data-testid="kypa-answer-callout">
            <div className="text-xs font-bold tracking-[2px] text-amber-400 mb-3">THE KYPA ANSWER</div>
            <p className="text-slate-200 leading-relaxed mb-3">
              A KYPA credential caps <code className="text-cyan-300 text-xs">max_amount_cents</code> + <code className="text-cyan-300 text-xs">category</code> + <code className="text-cyan-300 text-xs">recipient_allowlist</code> + <code className="text-cyan-300 text-xs">expires_at</code> at mandate issuance. When volume spikes, the mandate cap holds: the agent either refreshes with a fresh cardholder-signed mandate (bounded expansion) or refuses the call at the KYPA verify edge (never touches the card).
            </p>
            <p className="text-slate-200 leading-relaxed">
              <strong className="text-white">Delegation size becomes dynamic, not worst-week.</strong> No idle capital drag. No runaway spend. And every accepted call carries an on-chain evidence record the buyer can point at during reconciliation.
            </p>
          </div>
          <p className="text-xs text-slate-500 mt-6 italic max-w-3xl">
            Framing drawn from public September 2026 commentary by an agentic-commerce industry voice announcing an agent-initiated payment route on a KYB identity API. Institutional attribution only.
          </p>
        </div>
      </section>

      {/* RAIL COMPOSITION */}
      <section className="py-14 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-emerald-300 mb-3">HOW IT COMPOSES WITH YOUR EXISTING B2B STACK</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">One header. One verify call. Zero new integrations.</h2>
          <div className="grid md:grid-cols-3 gap-4" data-testid="composition-grid">
            <div className="border border-white/10 rounded-xl p-5 bg-slate-900/40">
              <div className="text-lg font-bold text-white mb-2">Your ERP / P2P system</div>
              <p className="text-sm text-slate-400 leading-relaxed">SAP Ariba, Coupa, Oracle Procurement, Workday, NetSuite: every one already emits a structured PO. KYPA credential rides alongside the PO as a signed rider. Nothing else changes.</p>
            </div>
            <div className="border border-white/10 rounded-xl p-5 bg-slate-900/40">
              <div className="text-lg font-bold text-white mb-2">Your payment rails</div>
              <p className="text-sm text-slate-400 leading-relaxed">ACH, wire, card, stablecoin, x402, MPP. KYPA verify runs at the supplier&rsquo;s edge or the issuer&rsquo;s authorization callback: before the rail is consulted. Rails stay unchanged.</p>
            </div>
            <div className="border border-white/10 rounded-xl p-5 bg-slate-900/40">
              <div className="text-lg font-bold text-white mb-2">Your reconciliation queue</div>
              <p className="text-sm text-slate-400 leading-relaxed">Every KYPA-attested transaction produces an on-chain evidence record (mandate hash + settlement tx + slash tx if applicable), exportable as a machine-readable audit packet under a minute.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-6 bg-slate-900/60 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-4">HOW TO INTEGRATE</div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Add one request header (<code className="text-cyan-300 text-xs">X-KYPA-Credential</code>) to your existing PO issuance and payment-authorization calls. Call the KYPA verify endpoint once from the supplier&rsquo;s invoice-acceptance webhook or your ERP&rsquo;s payment-authorization hook. Settle only if <code className="text-cyan-300 text-xs">kypa_ok</code> is true.
          </p>
          <p className="text-slate-300 leading-relaxed mb-6">
            The one-page integration brief walks the exact endpoints and signature-verification flow. Full spec at <Link to="/kypa/instrument" className="text-cyan-300 hover:underline">/kypa/instrument</Link>. Merchant-side prevention math at <Link to="/for-merchants" className="text-cyan-300 hover:underline">/for-merchants</Link>. Issuer-side callback composition at <Link to="/for-issuers" className="text-cyan-300 hover:underline">/for-issuers</Link>.
          </p>
          <a href="/downloads/kypa-facilitator-integration-brief.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-md px-4 py-2" data-testid="brief-download">
            Download the one-page integration brief <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
